/* External */
import {
  memo, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router';

/* Components */
import createAccountBackgroundImage from '~/assets/images/createAccountBg.png';
import logoDgoDirectv from '~/assets/svg/logos-dgo-directv.svg';
import logoDgoSkyMais from '~/assets/svg/logos-dgo-skymais.svg';
import Steps from './Steps';
import QrCode from '../../../components/QrCode';
import Hint from '../../../components/Hint';
import FocusableButton from '../../../components/FocusableButton';

/* Providers */
import { useCountry, useIsBrazil } from '../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';

/* Utils */
import { history } from '../../../utils/history';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import { createAccountUrlBR, createAccountUrlSSLA } from '../../../utils/env';
import { isCreateAccountActiveForCountry } from '../../../utils/profiles';
import { getLink, Routes } from '../../../utils/router';

/* Styles */
import './CreateAccount.scss';

function CreateAccount({ realFocusKey, setFocus }) {
  const country = useCountry();
  const isBrazil = useIsBrazil();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);

  const GO_BACK_BUTTON_FOCUS_KEY = `${realFocusKey}-go-back`;
  const redirectTo = getLink(Routes.Public.WelcomePage);

  const onBackPress = useCallback(() => {
    history.goBack();
  }, []);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.LOGIN_DATA,
        subCategory: GTM_SUBCATEGORIES.LOGIN.REGISTER,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsMountRef.current = false;
    }
  }, [country, commonTaggingEvent]);

  useEffect(() => {
    setFocus(GO_BACK_BUTTON_FOCUS_KEY);
  }, [setFocus, GO_BACK_BUTTON_FOCUS_KEY]);

  if (!isCreateAccountActiveForCountry(country)) return <Redirect to={redirectTo} />;

  return (
    <div className="dtv-html5-c-create-account">
      <img
        className="dtv-html5-c-create-account__image"
        src={createAccountBackgroundImage}
        alt="Create-Account"
      />
      <section className="dtv-html5-c-create-account__content">
        <div className="dtv-html5-c-create-account__content-container">
          <img
            className="dtv-html5-c-create-account__content-logo"
            src={isBrazil ? logoDgoSkyMais : logoDgoDirectv}
            alt="logo-dgo-directv"
          />
          <h2 className="dtv-html5-c-create-account__content-title">
            <FormattedMessage id="createAccount.title" />
          </h2>

          <Steps />
        </div>
        <div className="dtv-html5-c-create-account__content-container">
          <QrCode
            data={isBrazil ? createAccountUrlBR : createAccountUrlSSLA}
            className="dtv-html5-c-create-account__content-qr-code"
          />
        </div>
      </section>
      <FocusableButton
        focusKey={GO_BACK_BUTTON_FOCUS_KEY}
        onEnterPress={onBackPress}
        className="dtv-html5-c-hint"
      >
        <Hint title="createAccount.hintTitle" icon="back" />
      </FocusableButton>
    </div>
  );
}

CreateAccount.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(CreateAccount)));
